import { doc, orderBy, collection, where, limit, query, getDocs } from "firebase/firestore";
import { db, functions } from "./firebase";
import { httpsCallable } from "firebase/functions";

// ============================================================================
// CACHING UTILITY WITH PROMISE DEDUPLICATION
// ============================================================================

// Global variable to hold the pending network request
let categoriesFetchPromise = null;

const getCachedActiveCategories = async (storeId, options = {}) => {
    // Unique cache key based on limits/sorts
    // Simple key for standard active categories fetch
    const cacheKey = `activeCategories_${storeId}`;

    // 1. Check In-Memory Promise (Deduplication)
    // If a request is already running, return that existing promise to prevent race conditions
    if (categoriesFetchPromise) {
        return categoriesFetchPromise;
    }

    // 2. Check Session Storage (Fast Cache)
    const cached = sessionStorage.getItem(cacheKey);
    const cachedTs = sessionStorage.getItem(`${cacheKey}_ts`);

    // 10 Minute Cache (Categories rarely change)
    if (cached && cachedTs && (Date.now() - parseInt(cachedTs) < 10 * 60 * 1000)) {
        return JSON.parse(cached);
    }

    // 3. Start New Network Request
    categoriesFetchPromise = (async () => {
        try {
            const { sortBy = "createdAt", sortDirection = "desc", limit: queryLimit = null } = options;

            const categoriesRef = collection(db, "stores", storeId, "categories");

            // Build query
            let queryConstraints = [
                where("status", "==", "active"),
                orderBy(sortBy, sortDirection)
            ];

            // If a specific limit is requested, we might want to just fetch ALL active categories 
            // and cache them, then slice in memory. This prevents cache fragmentation.
            // For now, let's just fetch them all (usually < 50 categories per store).

            const querySnapshot = await getDocs(query(categoriesRef, ...queryConstraints));

            const categories = [];
            querySnapshot.forEach((doc) => {
                categories.push({
                    id: doc.id,
                    ...doc.data()
                });
            });

            // Save to Cache
            sessionStorage.setItem(cacheKey, JSON.stringify(categories));
            sessionStorage.setItem(`${cacheKey}_ts`, Date.now().toString());

            return categories;
        } catch (error) {
            console.error('Error fetching active store categories:', error);
            throw error;
        } finally {
            // Clear the promise so future calls (after 10 mins) can fetch again
            categoriesFetchPromise = null;
        }
    })();

    return categoriesFetchPromise;
};

// Helper to invalidate cache on writes (create/update/delete)
const invalidateCategoryCache = (storeId) => {
    sessionStorage.removeItem(`activeCategories_${storeId}`);
    categoriesFetchPromise = null;
};

// ============================================================================
// EXPORTED FUNCTIONS
// ============================================================================

/**
 * Get active categories (Optimized with caching)
 */
export const getActiveStoreCategories = async (storeId, options = {}) => {
    try {
        if (!storeId) throw new Error("Store ID is required to get categories");

        // Use the cached/deduplicated fetcher
        const categories = await getCachedActiveCategories(storeId, options);

        // Apply limit in memory if needed (since we cache the full list)
        if (options.limit && categories.length > options.limit) {
            return categories.slice(0, options.limit);
        }

        return categories;
    } catch (error) {
        console.error('Error getting active store categories:', error);
        throw error;
    }
};

/**
 * Get categories for a specific store (Admin/Internal - No Caching enforced)
 */
export const getStoreCategories = async (storeId) => {
    try {
        const categoriesRef = collection(db, "stores", storeId, "categories");
        const querySnapshot = await getDocs(query(categoriesRef, orderBy("name")));
        const categories = [];
        querySnapshot.forEach((doc) => {
            categories.push({ id: doc.id, ...doc.data() });
        });
        return categories;
    } catch (error) {
        throw error;
    }
};

/**
 * Create a new category (SECURE - Server-side)
 */
export const createCategory = async (storeId, categoryData) => {
    try {
        if (!storeId) throw new Error("Store ID is required");
        const fn = httpsCallable(functions, 'createCategory');
        const result = await fn({ storeId, categoryData });

        if (result.data.success) {
            invalidateCategoryCache(storeId); // Invalidate Cache
            return result.data.categoryId;
        }
        throw new Error(result.data.message || 'Failed to create category');
    } catch (error) {
        console.error('Error creating category:', error);
        throw error;
    }
};

/**
 * Update a category (SECURE - Server-side)
 */
export const updateCategory = async (storeId, categoryId, categoryData) => {
    try {
        if (!storeId || !categoryId) throw new Error("IDs required");
        const fn = httpsCallable(functions, 'updateCategory');
        const result = await fn({ storeId, categoryId, categoryData });

        if (result.data.success) {
            invalidateCategoryCache(storeId); // Invalidate Cache
            return result.data;
        }
        throw new Error(result.data.message || 'Failed to update category');
    } catch (error) {
        console.error('Error updating category:', error);
        throw error;
    }
};

/**
 * Delete a category (SECURE - Server-side)
 */
export const deleteCategory = async (storeId, categoryId) => {
    try {
        if (!storeId || !categoryId) throw new Error("IDs required");
        const fn = httpsCallable(functions, 'deleteCategory');
        const result = await fn({ storeId, categoryId });

        if (result.data.success) {
            invalidateCategoryCache(storeId); // Invalidate Cache
            return result.data;
        }
        throw new Error(result.data.message || 'Failed to delete category');
    } catch (error) {
        console.error('Error deleting category:', error);
        throw error;
    }
};

// ============================================================================
// MULTILINGUAL HELPERS (Memory Only)
// ============================================================================

export const getLocalizedCategory = (category, language = 'en') => {
    if (!category) return null;
    const localizedCategory = { ...category };
    if (category.multilingual && category.multilingual[language]) {
        localizedCategory.name = category.multilingual[language].name || category.name;
        localizedCategory.description = category.multilingual[language].description || category.description;
    }
    return localizedCategory;
};

export const getLocalizedCategoryName = (category, language = 'en') => {
    if (!category) return '';
    if (category.multilingual && category.multilingual[language]?.name) {
        return category.multilingual[language].name;
    }
    return category.name || '';
};

export const getLocalizedCategories = (categories, language = 'en') => {
    return categories.map(category => getLocalizedCategory(category, language));
};